import { addDevVars } from "@/dev/initDev.ts";
import { notifyError } from "@/modules/error/errorReporting.ts";
import { languageAtom, localeMessages } from "@/modules/l10n/i18n.store.ts";
import { i18n } from "@lingui/core";
import { I18nProvider } from "@lingui/react";
import { getOwn } from "@warrenio/utils/collections/getOwnProperty";
import { useAtomValue } from "jotai/react";
import { atom } from "jotai/vanilla";
import { useLayoutEffect, type ReactNode } from "react";

const loadLanguageAtom = atom(async (get) => {
    const language = get(languageAtom);

    const importer = getOwn(localeMessages, language);
    if (importer === undefined) {
        console.error("Unsupported language: %s", language);
        return;
    }

    if (importer !== null) {
        console.debug("Loading language: %s", language);
        try {
            const { messages } = await importer();
            i18n.load(language, messages);
        } catch (e) {
            notifyError(e);
            // This atom can not fail since almost every component depends on it, so just swallow the error and keep the default locale.
            return;
        }
    }

    return language;
});

export function ProvideLanguage({ children }: { children: ReactNode }) {
    return <I18nProvider i18n={i18n}>{children}</I18nProvider>;
}

export function LoadLanguage({ children }: { children: ReactNode }) {
    // This will suspend the entire app until the language is loaded
    const language = useAtomValue(loadLanguageAtom);
    useLayoutEffect(() => {
        if (language) {
            i18n.activate(language);
        }
    }, [language]);

    return children;
}

addDevVars({ i18n });
